import React from 'react';
import MainPageState from './main-page-states';
import { observer } from 'mobx-react-lite';
import { ExpandLess, ExpandMore, MenuOpenOutlined, MenuOutlined } from '@mui/icons-material';
import { Paper, List, Collapse, ListItemButton, ListItemIcon, ListItemText, Box } from '@mui/material';
import { Redirect, Route, Switch } from '../../router/react-router-v5-compat';
import { PermissionManager } from '../../router/permission.manager';
// import Scroll from '../../component/scroll/scroll.component';
import ToolBarAvatar from '../../component/top-bar/tool-bar-avatar.component';
import { RoutesType } from '../../router.config';
import { createHashHistory } from 'history';
import { accountState } from '../my-account/account-state';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import i18n from '../../i18n';
import { LinearDeterminate } from '../../component/linear-determinate';
import { CustomerLoading } from '../../component/loading/loading';
import SelectRestauantList from '../../component/select-restaurant/select-restaurtant-list';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Logo } from '../../static/images/svg/SMARTWAY-light.svg';
import { hasRestaurantWarrantAccess } from '../../router/restaurant-warrant.manager';
import { hasDualAnalysisTunnel } from '../../utils/restaurant-database-port';
import { isFiskalyAvailableForRestaurant } from '../../utils/restaurant-route-access';

const mainPageData = MobxObjectsManager.get(MainPageState)

/**
 * 无授权占位内容，用于在当前页面内容区提示餐厅暂无授权。
 * @returns 无授权文字
 */
const NoAuthContent = () => {
    return (
        <Box
            sx={{
                minHeight: 'calc(100vh - 160px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
            }}
        >
            <ListItemText
                primary={i18n.t('restaurant_warrant_page_text_no_auth')}
                primaryTypographyProps={{
                    variant: 'h4',
                    color: 'var(--text-dark)',
                    textAlign: 'center',
                }}
            />
        </Box>
    );
}

type RestaurantWarrantRouteContentProps = {
    /** 当前命中的业务路由 */
    route: RoutesType;
    /** React Router 传入的页面 props */
    routeProps: Record<string, unknown>;
    /** 当前餐厅 string_id，用于授权校验 */
    currentRestaurantStringId?: string;
    /** 当前餐厅数据库端口，用于双隧道页面访问校验 */
    currentRestaurantDatabasePort?: unknown;
    /** 当前餐厅国际电话区号，用于 Fiskaly 页面访问校验 */
    currentRestaurantCountryCode?: string;
}

/**
 * 子路由内容守卫：校验餐厅授权与双隧道条件，并渲染页面、占位或默认首页跳转。
 * @param props - 路由与当前餐厅上下文
 * @returns 目标页面、无授权占位，或校验中的空内容
 */
const RestaurantWarrantRouteContent = (props: RestaurantWarrantRouteContentProps) => {
    const {
        route,
        routeProps,
        currentRestaurantStringId,
        currentRestaurantDatabasePort,
        currentRestaurantCountryCode,
    } = props;
    // 当前子路由的授权校验状态。
    const [guardStatus, setGuardStatus] = React.useState<'checking' | 'allowed' | 'denied'>(() => route.requiredRestaurantWarrantCode ? 'checking' : 'allowed');

    React.useEffect(() => {
        // 标记当前异步校验是否已经过期，避免旧结果覆盖新餐厅状态。
        let disposed = false;

        /**
         * 校验当前子路由在当前餐厅下是否具备授权。
         */
        const checkRouteAccess = async () => {
            if (!route.requiredRestaurantWarrantCode) {
                setGuardStatus('allowed');
                return;
            }

            setGuardStatus('checking');
            const hasAccess = await hasRestaurantWarrantAccess(route.path, currentRestaurantStringId);

            if (!disposed) {
                setGuardStatus(hasAccess ? 'allowed' : 'denied');
            }
        };

        void checkRouteAccess();

        return () => {
            disposed = true;
        };
    }, [route.path, route.requiredRestaurantWarrantCode, currentRestaurantStringId]);

    if (
        (route.visibleWhenHasDualAnalysisTunnel || route.visibleWhenSpain)
        && !currentRestaurantStringId
    ) {
        return (
            <Box
                sx={{
                    position: 'relative',
                    minHeight: 'calc(100vh - 160px)',
                    width: '100%',
                    flex: 1,
                }}
                role="status"
                aria-live="polite"
                aria-label={i18n.t('general_text_loading')}
            >
                <CustomerLoading />
            </Box>
        );
    }

    if (
        route.visibleWhenHasDualAnalysisTunnel
        && !hasDualAnalysisTunnel(currentRestaurantDatabasePort)
    ) {
        return <Redirect to={PermissionManager.getDefaultHomePath(accountState.accountType)} />;
    }

    if (
        route.visibleWhenSpain
        && !isFiskalyAvailableForRestaurant(currentRestaurantCountryCode)
    ) {
        return <Redirect to={PermissionManager.getDefaultHomePath(accountState.accountType)} />;
    }

    if (guardStatus === 'checking') {
        /** 与 `NoAuthContent` 同高，保证 `CustomerLoading` 绝对定位有明确区域、转圈视觉居中 */
        return (
            <Box
                sx={{
                    position: 'relative',
                    minHeight: 'calc(100vh - 160px)',
                    width: '100%',
                    flex: 1,
                }}
                role="status"
                aria-live="polite"
                aria-label={i18n.t('general_text_loading')}
            >
                <CustomerLoading />
            </Box>
        );
    }

    if (guardStatus === 'denied') {
        return <NoAuthContent />;
    }

    // 当前路由实际要渲染的业务组件。
    const Component = route.component as React.ComponentType<Record<string, unknown>>;

    return <Component {...routeProps} />;
}

const MainPage = observer((props: Props) => {
    useTranslation()

    const { sidebarWidth, open, isMobileDevice, openTabletTabIds, openTabIds, collapseState, belongedRestaurant, currentRestaurant, accountType } = mainPageData;

    const { routes } = props;

    const { expand, reduced, hidden } = sidebarWidth;
    const sWidth = open ? expand : isMobileDevice ? hidden : reduced;
    // 自动获取宽度收缩侧边栏
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up('md'));
    React.useEffect(() => {
        if (!isMobileDevice) {
            if (matches !== open) {
                mainPageData.expendSiderbar(!open)
            }
        }
    }, [matches])

    React.useEffect(() => {
        mainPageData.init();
        mainPageData.setProps(props);
        return () => { mainPageData.destroy() }
    }, [])

    const domRefs = React.useRef<{ [key: string]: number }>({})

    /**
     * 侧边栏
     * @param item
     * @param hideIcon
     * @param zIndex
     * @returns
     */
    const renderSidebar = (item: RoutesType, hideIcon?: boolean, zIndex: number = 0, isChildren?: boolean) => {
        if (item.isHideNav) {
            return null
        }
        if (accountState.accountType && ['ADMIN', 'RESELLER', 'STAFF'].includes(accountState.accountType)) {
            if (item.only && !item.only?.includes(accountState.accountType)) {
                return null
            }
            // 仅西班牙餐厅显示 Fiskaly 等入口，避免非西班牙商家看到无效设置页
            if (item.visibleWhenSpain && !isFiskalyAvailableForRestaurant(currentRestaurant.country_code)) {
                return null
            }
            // 无负责集团时不显示集团营收等依赖集团上下文的入口
            if (item.visibleWhenHasResponsibleGroup && !(accountState.userInfo?.responsible_group?.length)) {
                return null
            }
            if (item.visibleWhenHasDualAnalysisTunnel && !hasDualAnalysisTunnel(currentRestaurant.database_port)) {
                return null
            }
        } else {
            return null
        }

        const pathname = createHashHistory()?.location?.pathname;

        const zIndexLeft = zIndex === 0 ? '60px' : '0px';
        const zIndexBoxShadow = zIndex === 0 ? '' : 'noShadow';
        const zIndexBackdrop = zIndex === 1 ? 'collapse-box-non-back' : 'collapse-box';
        if (item.children && item.isTab) {
            return (
                <div ref={(refs) => { domRefs.current[item.id] = refs?.offsetTop || 0; }}
                    key={item.id} className={`${isMobileDevice ? '' : 'nav-item'} ${isMobileDevice && openTabletTabIds.includes(item.id) ? 'tabletTabIds' : ''} ${zIndexBoxShadow}`}>
                    <ListItemButton onClick={() => { ((isMobileDevice && !open) || open === true) && mainPageData.fold(item.id) }} className={`nav-item-parents ${pathname.includes(item.path) ? 'active-nav-item-parents' : ''}`}>
                        {!hideIcon && item.icon && (
                            <ListItemIcon className="default-icon">
                                {item.icon()}
                            </ListItemIcon>
                        )}
                        <ListItemText className='title' primary={i18n.t(item.name)} />
                        {open === false ? null : (openTabIds.includes(item.id)) ? <ExpandLess className='expandIcon' /> : <ExpandMore className='expandIcon' />}
                    </ListItemButton>
                    <Collapse
                        in={open === false ? true : openTabIds.includes(item.id)}
                        timeout={collapseState ? 0 : 'auto'}
                        unmountOnExit
                        className={`${zIndexBackdrop}`}
                        style={{ left: zIndexLeft, maxHeight: !open ? `calc(100vh - ${domRefs.current[item.id] + 40 + 20}px)` : 'unset' }}
                    >
                        <List component='div' disablePadding >
                            {item.children.map((childrenItem) => {
                                return (
                                    renderSidebar(childrenItem, false, zIndex + 1, true)
                                )
                            })}
                        </List>
                    </Collapse>
                </div>
            )
        } else {
            return (
                <ListItemButton className={`${isMobileDevice ? '' : 'nav-item'}  ${isChildren ? 'nav-item-children' : null} ${pathname.includes(item.path) ? 'active' : ''} `} key={item.id}
                    onClick={() => { mainPageData.clickSidebar(item); }}>
                    {!hideIcon && item.icon && (
                        <ListItemIcon className="default-icon">
                            {item.icon()}
                        </ListItemIcon>
                    )}
                    <ListItemText className={`title ${pathname.includes(item.path) ? 'active-title-text' : ''}`} primary={i18n.t(item.name)} />
                </ListItemButton>
            )
        }
    }

    return (
        <div className='app-shell'>
            {/* 手机模式侧边栏蒙版 */}
            {isMobileDevice && <div className='mask' style={{ opacity: open ? 1 : 0, visibility: open ? 'visible' : 'hidden' }}
                onClick={() => { mainPageData.expendSiderbar(false) }}>
            </div>}
            <LinearDeterminate />
            <div
                className={`sidebar-unique  ${!open ? 'sidebar-closed' : ''} ${isMobileDevice ? 'sidebar-closed-mobile' : ''}`}
                style={{ width: sWidth }}
            >
                <Paper className={`foldBtn boxShadow`} style={isMobileDevice ? { opacity: 1 } : { opacity: 1 }}
                    onClick={() => {
                        mainPageData.expendSiderbar(!open)
                    }}>
                    {open ? <MenuOpenOutlined /> : <MenuOutlined />}
                </Paper>
                <div className='nav-bar nav-background'>
                    {/* 导航栏头部 */}
                    <div className='nav-header' >
                        <ToolBarAvatar isHiddenText={open} />
                    </div>
                    {/* 餐厅选择框 */}
                    {accountType === 'STAFF' && <div className='nav-header'>
                        <SelectRestauantList
                            isHiddenText={!open}
                            currentRestaurant={currentRestaurant}
                            items={belongedRestaurant?.map((item) => ({
                                label: item.nickname || '',
                                value: item.string_id || ''
                            }))}
                            value={currentRestaurant.string_id || ''}
                            onChange={(value) => {
                                const data = belongedRestaurant?.find((item) => item.string_id === value)
                                if (data) {
                                    mainPageData.setCurrentRestaurant(data);
                                }
                            }}
                        />
                    </div>}
                    {/* 导航栏列表 */}
                    <List
                        component='nav'
                        aria-labelledby='nested-list-subheader'
                        style={{ paddingBottom: open ? '0' : '40vh', overflow: open ? 'auto' : 'unset', position: 'unset' }}
                    >
                        {mainPageData.routes.map((item) => {
                            return renderSidebar(item);
                        })}
                    </List>
                    <Box display='flex' justifyContent='center' sx={{ mb: '40px' }}>
                        <Logo width={120} />
                    </Box>
                </div>
            </div>
            <div className={`content-page ${!open ? 'sidebar-closed-content-page' : ''} ${isMobileDevice ? 'sidebar-closed-content-page-mobile' : ''}`}  >
                <div className={`content-page-container`}>
                    <Switch>
                        {mainPageData.flatRoutes(routes).filter((route) => route.component && route.component !== '').map((route) => {
                            return (
                                <Route
                                    key={`${route.id}-${currentRestaurant.string_id || ''}`}
                                    path={route.path}
                                    exact={route.exact !== false}
                                    render={(routeProps) => (
                                        <RestaurantWarrantRouteContent
                                            route={route}
                                            routeProps={{ routes: route?.children, ...routeProps }}
                                            currentRestaurantStringId={currentRestaurant.string_id}
                                            currentRestaurantDatabasePort={currentRestaurant.database_port}
                                            currentRestaurantCountryCode={currentRestaurant.country_code}
                                        />
                                    )}
                                />)
                        })}
                        {/* /main 下无匹配子路由时回默认首页，避免内容区空白 */}
                        <Redirect to={PermissionManager.getDefaultHomePath(accountState.accountType)} />
                    </Switch>
                </div>
            </div>
        </div >
    )
})

type Props = {
    routes: RoutesType[];
}

export default MainPage;